import { ValidatedForm } from "components/forms";
import { AccountTypes } from "features/Accounts/types";
import EnrollmentBuilder, {
  type EnrollmentBuilderValues,
} from "features/People/Groups/AddEditGroupForms/EnrollmentBuilder/EnrollmentBuilder";
import { getDataSourceOptions } from "features/People/Groups/AddEditGroupForms/EnrollmentBuilder/options";
import { type EnrollmentRule } from "features/People/Groups/AddEditGroupForms/EnrollmentBuilder/utils";
import { noop } from "lodash";
import React, { useMemo } from "react";
import { validationSchemas } from "utils";

import styles from "./groupTemplateEnrollment.module.scss";
import { type ValidatedFieldProps } from "components/forms/ValidatedForm";
import NoResults from "components/noResults/NoResults";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsersGear } from "@fortawesome/pro-solid-svg-icons";

interface Props {
  disabled?: boolean;
  onIsDirtyChange?: (isDirty: boolean) => void;
  onIsValidChange: (isValid: boolean) => void;
  enrollmentRules: EnrollmentRule[];
  templateUsersOnly: boolean | null;
  bindGetValues: (getValues: () => { enrollmentRules: EnrollmentRule[]; templateUsersOnly: boolean | null }) => void;
  bindFormReset?: (resetForm: () => void) => void;
  disablePreventTransitionPrompt?: boolean;
  accountType: number;
}

const DirtyObserver = (
  props: React.PropsWithChildren<{ onIsDirtyChange?: (isDirty: boolean) => void; isDirty: boolean }>,
) => {
  const { onIsDirtyChange, isDirty, children } = props;

  React.useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return <>{children}</>;
};

export const GroupTemplateEnrollment = (props: Props) => {
  const {
    disabled,
    onIsValidChange,
    enrollmentRules,
    templateUsersOnly,
    bindGetValues,
    onIsDirtyChange,
    bindFormReset,
    disablePreventTransitionPrompt,
    accountType,
  } = props;
  const initialValues = useMemo(() => ({ enrollmentRules, templateUsersOnly }), [enrollmentRules, templateUsersOnly]);
  const showMsGraphSource = useMemo(
    () => [AccountTypes.BSI, AccountTypes.MSP, AccountTypes.ChannelPartner].includes(accountType),
    [accountType],
  );

  const ruleLengthWithoutNulls = useMemo(
    () => enrollmentRules.filter((e) => e.filter !== null).length,
    [enrollmentRules],
  );

  if (ruleLengthWithoutNulls === 0 && disabled) {
    return (
      <NoResults
        title="No enrollment rules configured"
        description="It seems the owner of this inherited group hasn't set up any enrollment rules yet."
        icon={<FontAwesomeIcon icon={faUsersGear} className="no-results-icon" style={{ margin: 0 }} />}
      />
    );
  }

  return (
    <ValidatedForm
      className={styles.root}
      validationSchema={validationSchemas.automaticEnrollment}
      initialValues={initialValues}
      formWidthComputer={16}
      onSubmit={noop}
      bindGetValues={bindGetValues}
      bindFormReset={bindFormReset}
      isInitialValid={undefined}
      validateOnMount
      disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      enableReinitialize
    >
      {(formik) => (
        <DirtyObserver isDirty={formik.validatedFieldProps.dirty} onIsDirtyChange={onIsDirtyChange}>
          <EnrollmentBuilder
            disabled={!!disabled}
            values={formik.values}
            validatedFieldProps={formik.validatedFieldProps as ValidatedFieldProps<EnrollmentBuilderValues>}
            onIsValidChange={onIsValidChange}
            handleCriteriaChange={noop}
            dataSourceOptions={getDataSourceOptions(disabled || showMsGraphSource)}
          />
        </DirtyObserver>
      )}
    </ValidatedForm>
  );
};
